import { Link } from 'react-router-dom';
import { ca } from '../i18n/ca';
import {
  PLANNED,
  roadmapLink,
  stageOf,
  type PlannedId,
  type PlannedStage,
} from './plannedRegistry';
import { Shell } from './Shell';

const STAGES: PlannedStage[] = ['R3', 'R4', 'R5', 'R6'];

const reason = (why: 'panel' | 'ingame' | undefined) =>
  why === 'ingame' ? ca.planned.waitsGame : why === 'panel' ? ca.planned.waitsPanel : null;

/**
 * «Què ve» (`/que-ve`, G3b): everything the design asks for and the web does not do yet, one line
 * each with the reason it waits under it (the owner, test card 166: a list without the reasons
 * did not say why), by the stage of the roadmap it belongs to. It is the registry of the «Pendent» chips
 * (`plannedRegistry.ts`) read as a page, so it is never a second list to keep: a feature added to
 * the roadmap gets its entry and a chip, and shows here with no change to this file; one that is
 * built loses its entry and leaves the page. A line leads to the roadmap at its item when the
 * roadmap has one.
 *
 * Product content only: no step number, no id, nothing of what a mission keeps as a surprise.
 */
export function ComingUp() {
  const t = ca.planned.page;
  const ids = Object.keys(PLANNED) as PlannedId[];
  const stages = STAGES.map((stage) => ({
    stage,
    rows: ids.filter((id) => stageOf(PLANNED[id].step) === stage),
  })).filter((s) => s.rows.length > 0);

  return (
    <Shell title={t.title} width="wide">
      <p className="muted">{t.intro}</p>
      {stages.length === 0 && (
        <div className="empty">
          <i className="bi bi-signpost-split" aria-hidden="true" />
          <p className="muted">{t.empty}</p>
        </div>
      )}
      {stages.map(({ stage, rows }) => (
        <section key={stage} className="card coming-stage" aria-label={ca.planned.stages[stage]}>
          <div className="row between">
            <h2>{ca.planned.stages[stage]}</h2>
            <span className="chip num">{t.count(rows.length)}</span>
          </div>
          <ul className="plain">
            {rows.map((id) => {
              const entry = PLANNED[id];
              const why = reason('why' in entry ? entry.why : undefined);
              return (
                <li key={id} className="coming-row" data-planned-id={id}>
                  <div className="row">
                    <Link className="grow" to={roadmapLink(id)}>
                      {entry.label}
                    </Link>
                    <span className="chip planned">{ca.planned.chip}</span>
                    {why && <span className="chip">{why}</span>}
                  </div>
                  <p className="muted small coming-reason">
                    <span className="sr-only">{t.reasonLabel} </span>
                    {entry.reason}
                  </p>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
      <p className="muted small">
        <Link to="/full-de-ruta">
          <i className="bi bi-signpost-split" aria-hidden="true" /> {t.toRoadmap}
        </Link>
      </p>
    </Shell>
  );
}
